import { Loader, Theme } from '@afterpaytouch/core'

import { useTranslation } from 'next-i18next'
import React, { useEffect } from 'react'
import { Head } from '../../component'
import {
  useAppRouter,
  useCheckoutTheme,
  useAppDispatch,
  setConvergenceReadiness,
  useCohort,
  useRolloutFromClient,
  setLoginIdentityError,
  createRegistration,
  useCashEligibilitySignals,
  useCountry,
} from '../../state'
import {
  CheckoutPage,
  ConvergenceRollout,
  ConvergenceAvailable,
  ConvergenceUnavailable,
  Features,
  InitialLoadingRouteConvertedProps,
  LoginIdentityErrors,
  TrackingEvent,
  VerificationMethod,
  ConvergenceCashLogin,
} from '../../model'
import { Route } from '../../config/router'
import { getCohortTheme, isCookieEnabled } from '../../state/convergence/helper'
import { useFlag } from '@afterpaytouch/feature'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { unwrapResult } from '@reduxjs/toolkit'
import { Cohort, ErrorCode, isPortalApiError } from '@afterpaytouch/portal-api'
import { isCashAppInAppBrowser } from '../../utils/device'
import { convergenceThemes } from '../../model/convergence'

export interface InitialLoadingProps extends InitialLoadingRouteConvertedProps {
  rollout?: ConvergenceRollout
  cohort: Cohort | null
}

export const InitialLoadingPage: CheckoutPage<InitialLoadingProps> = ({
  rollout: rolloutFromServer = ConvergenceUnavailable.Unavailable,
  cohort: cohortFromServer,
  nextRoute,
  hasNoExistingLoginIdentity = false,
  isShadowAccount = false,
  isPersistentLogin = false,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const [, setTheme] = useCheckoutTheme()
  const canRetrieveCookies = isCookieEnabled()
  const isCookieRolloutFallbackEnabled = useFlag(Features.CONVERGENCE_COOKIE_FALLBACK_ENABLED, false)
  const cohortFromState = useCohort() ?? Cohort.APOnly
  const handleCookieFallback = isCookieRolloutFallbackEnabled && !canRetrieveCookies
  const locale = router.locale
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isCashAppIAB = isCashAppInAppBrowser()
  const country = useCountry()

  const getRolloutFromClient = useRolloutFromClient()
  const cashEligibilitySignals = useCashEligibilitySignals()

  const handleCreateRegistration = async (): Promise<Route> => {
    try {
      const { registrationStatus } = unwrapResult(await dispatch(createRegistration({ flow: VerificationMethod.EMAIL, locale })))
      return registrationStatus?.emailVerificationRequired ? Route.VERIFY : Route.SIGN_UP
    } catch (e) {
      // handle invalid email address error when create registration fails
      const code = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: code })
      dispatch(setLoginIdentityError(t(`login:error:${LoginIdentityErrors[code] ?? 'unknown'}`)))
      return Route.ENTER_EMAIL
    }
  }

  const getNextRoute = async (
    theme: Theme,
    cohort: Cohort | null,
    shadowAccountExists: boolean,
    isCashLoginEnabled: ConvergenceCashLogin,
    isPersistentLogin: boolean,
    rollout: ConvergenceRollout
  ): Promise<Route> => {
    const isCashLoginAllowed =
      !isPersistentLogin &&
      (isCashLoginEnabled === ConvergenceCashLogin.Enabled || (isCashLoginEnabled === ConvergenceCashLogin.DisabledNew && shadowAccountExists))
    const isRolloutEnabled = Object.values(ConvergenceAvailable).includes(rollout as ConvergenceAvailable)
    const shouldRouteToCashAuth = shadowAccountExists || isRolloutEnabled

    if (convergenceThemes.includes(theme) && shouldRouteToCashAuth && cohort === Cohort.CashOnly && !isCashAppIAB && isCashLoginAllowed) {
      return Route.CASH_AUTH
    }

    if (hasNoExistingLoginIdentity) {
      return await handleCreateRegistration()
    }

    return nextRoute ?? Route.TERMINAL_ERROR
  }

  const handleRouting = async (): Promise<void> => {
    let cohort = cohortFromServer
    let rollout = rolloutFromServer
    let cashLogin = ConvergenceCashLogin.DisabledNew
    // temp while in % rollout trigger rollout based on LD client side evaluation
    const convergenceRolloutLDClient = await getRolloutFromClient()
    cashLogin = convergenceRolloutLDClient.cashLogin

    if (handleCookieFallback) {
      // if cookies are accessible -> use the cohort/rollout from server, if not -> use the cohort/rollout from state/clientLD evaluation
      cohort = cohortFromState
      rollout = convergenceRolloutLDClient.rollout
    }

    dispatch(setConvergenceReadiness({ rollout, cohort, cashLogin }))

    const theme = getCohortTheme(rollout, isShadowAccount, country)
    setTheme(theme)
    const nextRoutePath = await getNextRoute(theme, cohort, isShadowAccount, cashLogin, isPersistentLogin, rollout)

    logEvent(TrackingEvent.VIEWED_INITIAL_LOADING_PAGE, {
      nextRoute,
      canRetrieveCookies,
      handleCookieFallback,
      customerCohort: cohort,
      checkoutBrand: theme,
      convergenceRollout: rollout,
      cashEligibilitySignals,
      cashLoginAvailability: cashLogin,
    })

    router.replace(nextRoutePath)
  }

  useEffect(() => {
    const init = async (): Promise<void> => {
      handleRouting()
    }

    init()
  }, [])

  return (
    <>
      <Head title={t('common:brandName')} />
      <Loader alignCenter withHeader={false} />
    </>
  )
}
